import { ErrorBox, Loading } from "@gearbox-protocol/deploy-tools-ui";
import { iAddressProviderV310Abi } from "@gearbox-protocol/sdk/abi/310/generated";
import { ierc20Abi } from "@gearbox-protocol/sdk/abi/iERC20";
import { iFaucetAbi } from "@gearbox-protocol/sdk/dev";
import {
  ADDRESS_0X0,
  ADDRESS_PROVIDER_V310,
  AddressSet,
  chains,
  getNetworkType,
} from "@gearbox-protocol/sdk/onchain";
import type { FC, PropsWithChildren } from "react";
import { createContext, useContext } from "react";
import { useAsync } from "react-use";
import type { Address, PublicClient } from "viem";
import { createPublicClient, defineChain, http, stringToHex } from "viem";
import { useUnpausedForkStatus } from "../context";

export interface FaucetAsset {
  address: Address;
  symbol: string;
  decimals: number;
}

export interface FaucetContext {
  address: Address;
  assets: FaucetAsset[];
}

const Ctx = createContext<FaucetContext>({
  address: ADDRESS_0X0,
  assets: [],
});

async function getFaucetAddress(
  client: PublicClient,
  addressProviders: Address[],
): Promise<Address> {
  let faucetAddr: Address | undefined;
  for (const address of addressProviders) {
    try {
      faucetAddr = await client.readContract({
        address,
        abi: iAddressProviderV310Abi,
        functionName: "getAddressOrRevert",
        args: [stringToHex("FAUCET", { size: 32 }), 0n],
      });
      break;
    } catch {}
  }
  if (!faucetAddr || faucetAddr === ADDRESS_0X0) {
    throw new Error("Faucet is not deployed on this fork");
  }
  return faucetAddr;
}

interface FaucetProviderProps {
  extraAssets?: Address[];
}

export const FaucetProvider: FC<PropsWithChildren<FaucetProviderProps>> = p => {
  const { extraAssets = [], children } = p;
  const { rpcUrl, originChainId, anvilChainId, addressProvider } =
    useUnpausedForkStatus();

  const { value, loading, error } = useAsync(async () => {
    const networkType = getNetworkType(originChainId);
    const chain = defineChain({
      ...chains[networkType],
      id: anvilChainId,
    });
    const client = createPublicClient({
      transport: http(rpcUrl),
      chain,
    });
    // Use address from sdk with address from testnet as fallback
    const address = await getFaucetAddress(client, [
      ADDRESS_PROVIDER_V310,
      addressProvider,
    ]);

    let assets = await client.readContract({
      address,
      abi: iFaucetAbi,
      functionName: "getAssets",
      args: [],
    });
    assets = new AddressSet([...assets, ...extraAssets]).asArray();

    const assetsInfo = await client.multicall({
      contracts: assets.flatMap(a => [
        {
          address: a,
          abi: ierc20Abi,
          functionName: "symbol",
        },
        {
          address: a,
          abi: ierc20Abi,
          functionName: "decimals",
        },
      ]),
      allowFailure: true,
    });

    return {
      address,
      assets: assets.map((a, i) => ({
        address: a,
        symbol: (assetsInfo[i * 2].result as string) ?? a,
        decimals: Number(assetsInfo[i * 2 + 1].result),
      })),
    };
  }, [rpcUrl, originChainId, anvilChainId, addressProvider, extraAssets]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorBox error={error} />;
  }

  return <Ctx.Provider value={value!}>{children}</Ctx.Provider>;
};

export const useFaucet = () => useContext(Ctx);
